import { t } from "@travily/i18n";
import type { ProgramsCardAccountWithMembersTrait } from "@travily/types/models/ProgramsCard";
import { Icon } from "@ui/components/Icon";
import { Image } from "@ui/components/Image";
import { SectionCard } from "@ui/components/SectionCard";
import { Link } from "@ui/primitives/Link";

type Member = ProgramsCardAccountWithMembersTrait["account"]["members"][number];

export interface TeamCardProps {
  members: Member[];
  className?: string;
}

/**
 * "Meet the Team" program section: the community account's people as playful
 * polaroid-style cards with alternating ±1° tilt. Members with a visible
 * individual profile link to it (overlay link, Card-style). Renders nothing
 * when the account has no presentable members. (Video hero comes later.)
 */
export function TeamCard({ members, className }: TeamCardProps) {
  if (members.length === 0) return null;

  return (
    <SectionCard className={className} theme="purple">
      <SectionCard.Content>
        <SectionCard.Header
          description={t("components.views.programs.show.team_description")}
          icon={<Icon className="size-8 xl:size-10" name="members" type="sticker" />}
          title={t("components.views.programs.show.team_title")}
        />
        <ul className="grid w-full grid-cols-[repeat(auto-fill,minmax(min(100%,10rem),1fr))] gap-4 xl:gap-7">
          {members.map((member, index) => (
            <li className={`relative ${index % 2 === 0 ? "rotate-1" : "-rotate-1"}`} key={member.id}>
              <figure className="flex w-full flex-col items-center gap-3 overflow-clip rounded-2xl bg-purple-100 p-3 shadow-[inset_0_-1px_0_0_rgba(0,0,0,0.1),inset_0_0_0_1px_var(--color-purple-400)]">
                <div className="aspect-square w-full overflow-clip rounded-xl">
                  {member.avatar ? (
                    <Image alt={member.avatar.alt} className="size-full object-cover" image={member.avatar} />
                  ) : (
                    <div
                      aria-hidden
                      className="flex size-full items-center justify-center bg-purple-200 font-extrabold text-4xl text-purple-700"
                    >
                      {member.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <figcaption className="flex w-full flex-col text-center">
                  <span className="font-extrabold text-lg text-purple-800 leading-6">{member.name}</span>
                  {member.title && <span className="text-purple-700 text-sm leading-5">{member.title}</span>}
                </figcaption>
              </figure>
              {member.profile_url && (
                <Link
                  aria-label={member.name}
                  className="absolute inset-0 rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
                  href={member.profile_url}
                />
              )}
            </li>
          ))}
        </ul>
      </SectionCard.Content>
    </SectionCard>
  );
}
